/**
 * PrizesDisplay Component
 *
 * Displays both players' prizes for transparency.
 * Shows during setup phase so both players can see what prizes are at stake.
 */

import { useGameStore } from '../stores/gameStore';
import { COPY } from '../copy';

function PrizesDisplay() {
  const myPrizes = useGameStore((state) => state.myPrizes);
  const opponentPrizes = useGameStore((state) => state.opponentPrizes);
  const playerId = useGameStore((state) => state.playerId);
  const gamePhase = useGameStore((state) => state.gamePhase);

  // Only show during setup and waiting phases
  if (gamePhase !== 'setup' && gamePhase !== 'waiting') {
    return null;
  }

  const hasMyPrizes = myPrizes.length > 0;
  const hasOpponentPrizes = opponentPrizes.length > 0;

  // Don't show if no prizes entered yet
  if (!hasMyPrizes && !hasOpponentPrizes) {
    return null;
  }

  return (
    <div className="bg-dark-charcoal border-2 border-neon-yellow shadow-glow-yellow rounded-lg p-6 mb-6">
      <h3 className="text-xl font-bold text-neon-yellow text-glow-yellow mb-4">
        {COPY.prizes.display.title}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Your Prizes */}
        <div>
          <h4 className="text-lg font-semibold text-neon-cyan mb-2">
            {COPY.prizes.display.yourPrizes}
          </h4>
          {hasMyPrizes ? (
            <ul className="space-y-2">
              {myPrizes.map((prize, index) => (
                <li
                  key={index}
                  className="flex items-start bg-dark-purple/50 border-2 border-neon-cyan/30 rounded-md p-3"
                >
                  <span className="flex-shrink-0 w-6 h-6 bg-neon-cyan text-dark-navy rounded-full flex items-center justify-center text-sm font-bold mr-3">
                    {index + 1}
                  </span>
                  <span className="text-gray-200">{prize}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400 italic">
              {COPY.prizes.display.emptyState}
            </p>
          )}
        </div>

        {/* Opponent's Prizes */}
        <div>
          <h4 className="text-lg font-semibold text-neon-purple mb-2">
            {COPY.prizes.display.opponentPrizes}
          </h4>
          {hasOpponentPrizes ? (
            <ul className="space-y-2">
              {opponentPrizes.map((prize, index) => (
                <li
                  key={index}
                  className="flex items-start bg-dark-purple/50 border-2 border-neon-purple/30 rounded-md p-3"
                >
                  <span className="flex-shrink-0 w-6 h-6 bg-neon-purple text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                    {index + 1}
                  </span>
                  <span className="text-gray-200">{prize}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400 italic">
              {COPY.prizes.display.secretState}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 p-3 bg-neon-yellow/10 border-2 border-neon-yellow/50 rounded-md">
        <p className="text-sm text-gray-300">
          {COPY.prizes.display.bonusRule}
        </p>
      </div>
    </div>
  );
}

export default PrizesDisplay;
